import { LocationSearchItem } from '@deckee/api-app-main';
import { Card, NavList } from '@deckee/deck-hand';
import React, { FC, useCallback } from 'react';
import { validateLatitudeLongitude } from '@deckee/utilities';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { telemetry } from '../../../../../services/backend';
import { captureEvent } from '../../../../../services/event-capture';
import { AppState, useAppDispatch } from '../../../../../services/store';
import {
  updatePoint,
  updatePointWithPlaceId,
} from '../../../../../services/store/modules/plan';
import { getLayerDefinitions } from '../../../../../services/map-layer';
import BasicDrawerLayout from '../../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../../layouts/components/LayoutBody/LayoutBody';
import IconWithTextLockup from '../../../../lockups/IconWithTextLockup/IconWithTextLockup';
import BlueCircleWrapper from '../../../../shared/BlueCircleWrapper/BlueCircleWrapper';
import { ReactSvg, map, location } from '../../../../shared/vector-images';
import LocationSearch from '../LocationSearchInput/LocationSearch';

const SearchLocationPage: FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { lat: mapLat, lon: mapLon } = useSelector(
    (state: AppState) =>
      state.appControls.currentMapParams.viewPortDetails.center,
  );

  const { pointId } = useParams<{ pointId: string }>();

  const handleChooseOnMap = () => {
    captureEvent({
      type: 'plan: choose location from map',
      payload: {},
    });

    telemetry(
      telemetry.INFO,
      `[Plan.handleChooseOnMap] Selected point from choose on map ${pointId}`,
    );

    history.push(`/maps/plan/details/choose-location/${pointId}`);
  };

  const handleSelectSearchLocationCard = async (
    searchItem: LocationSearchItem,
  ) => {
    captureEvent({
      type: 'plan: search location',
      payload: {
        location: searchItem,
      },
    });

    telemetry(
      telemetry.INFO,
      `[Plan.handleSelectSearchLocationCard] Selected point from search ${JSON.stringify(
        searchItem,
      )}`,
    );

    // If there is a placeId, update the point with the placeId
    if (searchItem?.placeId) {
      await dispatch(
        updatePointWithPlaceId({
          placeId: searchItem.placeId,
          id: pointId,
          name: searchItem.title,
        }),
      ).unwrap();
    } else if (
      searchItem?.latitude &&
      searchItem?.longitude &&
      validateLatitudeLongitude(searchItem?.latitude, searchItem?.longitude)
    ) {
      dispatch(
        updatePoint({
          id: pointId,
          latitude: searchItem.latitude,
          longitude: searchItem.longitude,
          name: searchItem.title,
        }),
      );
    } else {
      const errorMsg = `Error unable to obtained a valid point to update ${JSON.stringify(
        searchItem,
      )}`;
      telemetry(
        telemetry.ERROR,
        `[Plan.handleSelectSearchLocationCard] ${errorMsg}`,
      );
      throw new Error(errorMsg);
    }

    history.push(`/maps/plan/details/choose-location/${pointId}`);
  };

  // Filter out beach emergency number markers from search results and render with icons
  const renderResults = useCallback(
    (
      items: LocationSearchItem[],
      onSelect: (item: LocationSearchItem) => void,
    ) => {
      // Filter out BEN markers - only WeatherLocationSearch should show them
      const filteredItems = items.filter(
        (item) => item.markerType !== 'beachEmergencyNumberMarker',
      );

      if (!filteredItems || filteredItems.length === 0) {
        return null;
      }

      return (
        <NavList grouped>
          {filteredItems.map((searchItem) => {
            const key =
              searchItem.placeId || searchItem.markerId || searchItem.title;

            // Get marker configuration for proper icon and colors
            const markerConfig =
              getLayerDefinitions()[searchItem.markerType || ''];

            // Determine the icon to display
            const icon = markerConfig?.icon?.baseShape
              ? markerConfig.icon.baseShape()
              : location();

            // Determine the icon color
            const iconColor =
              markerConfig?.icon?.iconColor || theme.colors.secondary;

            // Determine the background color
            const bgColor = markerConfig?.icon?.iconColor
              ? markerConfig?.icon?.markerColor
              : 'white';

            return (
              <NavList.Item
                key={key}
                chevron
                onClick={() => onSelect(searchItem)}
              >
                <IconWithTextLockup
                  heading={searchItem.title}
                  description={
                    searchItem.placeDescription || markerConfig?.displayName
                  }
                  emphasis="heading"
                  useEllipsis
                  image={
                    <BlueCircleWrapper bg={bgColor}>
                      <ReactSvg
                        toDisplay={icon}
                        options={{
                          fillColor: iconColor,
                        }}
                        width={24}
                        height={24}
                      />
                    </BlueCircleWrapper>
                  }
                />
              </NavList.Item>
            );
          })}
        </NavList>
      );
    },
    [theme],
  );

  return (
    <BasicDrawerLayout
      heading="Search location"
      onBack={history.goBack}
      onClose={null}
      showMenuBar={false}
    >
      <LayoutBody>
        <LocationSearch
          onSelectLocation={handleSelectSearchLocationCard}
          searchCenterLat={mapLat}
          searchCenterLon={mapLon}
          placeholder="Search for a location"
          autoFocus
          renderResults={renderResults}
          additionalActions={
            <Card>
              <NavList grouped>
                <NavList.Item chevron onClick={handleChooseOnMap}>
                  <IconWithTextLockup
                    heading="Choose on map"
                    emphasis="heading"
                    image={
                      <BlueCircleWrapper>
                        <ReactSvg
                          toDisplay={map()}
                          options={{
                            fillColor: '#001A70',
                          }}
                          width={24}
                          height={24}
                        />
                      </BlueCircleWrapper>
                    }
                  />
                </NavList.Item>
              </NavList>
            </Card>
          }
        />
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default SearchLocationPage;
